import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import {
  ListControlBar,
  ViewTabs,
  type FacetDef,
  type GroupByOption,
  type PropertyChoice
} from '../../list-controls';
import {
  GROUP_BY_OPTIONS,
  type WorkItemsTab
} from '../types';
import { WorkItemsBoardTab } from '../../../pages/work-items/board/WorkItemsBoardTab';
import { BOARD_CHIP_KEYS, BOARD_CHIP_LABELS } from '../../../pages/work-items/board/types';
import { WorkItemsTableTab } from './WorkItemsTableTab';
import {
  LOCKED_COLS,
  PRIORITY_CHOICES,
  STATUS_CHOICES,
  TAB_LABELS,
  TAB_ORDER,
  WORK_ITEM_COLUMNS
} from './tableColumns';
import type { WorkItemsViewState } from './viewState';
import '../../../pages/WorkItems.css';

export interface WorkItemsViewProps {
  /** Injected by the host — URL-backed (standalone) or local (projects). */
  state: WorkItemsViewState;
  /** Org scope (every data tab filters on org_id). */
  activeOrgId: string | null;
  /** When set, every data tab additionally filters on project_id. */
  projectId?: string;
  /** Bumped by the host's EditorCover onClose → data tabs refetch in place. */
  reloadSignal: number;
  /** Host owns the EditorCover; the View just forwards row-open clicks. */
  onOpenItem: (id: string) => void;
  /** Inline-edit failure surfacing — host owns the Toast. */
  onToast: (message: string | null) => void;
  /** Tabs + panel + table + timeline testids (standalone: "work-items-route"). */
  testIdPrefix: string;
  /** ListControlBar testids (standalone: "work-items" — preserves existing specs). */
  controlBarTestIdPrefix: string;
}

export function WorkItemsView({
  state,
  activeOrgId,
  projectId,
  reloadSignal,
  onOpenItem,
  onToast,
  testIdPrefix,
  controlBarTestIdPrefix
}: WorkItemsViewProps) {
  const {
    tab,
    setTab,
    filters,
    setFilters,
    groupBy,
    setGroupBy,
    searchQuery,
    setSearchQuery,
    visibleCols,
    setVisibleCols,
    tableUrlAdapter
  } = state;

  // Epic list for the toolbar Epic facet + the table's Epic editor options.
  // Org-wide on /work-items; project-scoped when embedded so the facet/editor
  // list only this project's epics. Cheap one-shot query; reload on org/project
  // switch.
  const [epicChoices, setEpicChoices] = useState<
    Array<{ id: string; name: string }>
  >([]);

  useEffect(() => {
    if (!activeOrgId) {
      setEpicChoices([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      let query = supabase
        .from('work_items')
        .select('id, name')
        .eq('org_id', activeOrgId)
        .eq('type', 'epic');
      if (projectId) query = query.eq('project_id', projectId);
      const { data } = await query.order('name', { ascending: true });
      if (cancelled) return;
      setEpicChoices(
        (data ?? []).map((r) => ({
          id: r.id as string,
          name: ((r.name as string) ?? '').trim() || '(untitled)'
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, projectId]);

  // Org member roster for the +Filter popover's Assignee facet. Mirrors the
  // same `list_org_members` RPC the inline-edit picker uses inside the table.
  // Cheap one-shot query; reload on org switch.
  const [memberChoices, setMemberChoices] = useState<
    ReadonlyArray<{ id: string; displayName: string }>
  >([]);

  useEffect(() => {
    if (!activeOrgId) {
      setMemberChoices([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error: rpcErr } = await supabase.rpc('list_org_members', {
        p_org_id: activeOrgId
      });
      if (cancelled || rpcErr || !Array.isArray(data)) return;
      const next: Array<{ id: string; displayName: string }> = [];
      for (const m of data as ReadonlyArray<{
        user_id?: string | null;
        display_name?: string | null;
        username?: string | null;
      }>) {
        if (!m.user_id) continue;
        const name = m.display_name?.trim() || m.username?.trim() || '';
        if (name) next.push({ id: m.user_id, displayName: name });
      }
      next.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setMemberChoices(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  // Facets exposed by the +Filter popover. Type / Status / Priority are
  // hardcoded enums; Epic loads from the one-shot query above; Assignee comes
  // from the `list_org_members` roster (same canonical source the inline-edit
  // person picker reads from). Sprint stays scaffolded as a "Coming soon" facet
  // until the schema lands.
  const facets: ReadonlyArray<FacetDef> = useMemo(
    () => [
      {
        key: 'type',
        label: 'Type',
        options: [
          { id: 'epic', label: 'Epic' },
          { id: 'story', label: 'Story' },
          { id: 'bug', label: 'Bug' },
          { id: 'task', label: 'Task' }
        ]
      },
      {
        key: 'status',
        label: 'Status',
        options: STATUS_CHOICES.map((s) => ({ id: s, label: s }))
      },
      {
        key: 'assignee',
        label: 'Assignee',
        options: memberChoices.map((m) => ({ id: m.id, label: m.displayName }))
      },
      {
        key: 'epic',
        label: 'Epic',
        options: epicChoices.map((e) => ({ id: e.id, label: e.name }))
      },
      {
        key: 'priority',
        label: 'Priority',
        options: PRIORITY_CHOICES.map((p) => ({ id: p.value, label: p.label }))
      },
      // Sprint stays as a "Coming soon" placeholder — no underlying schema
      // yet. URL round-trip is skipped until `work_item_property_defs`
      // surfaces it.
      { key: 'sprint', label: 'Sprint', options: [], comingSoon: true }
    ],
    [epicChoices, memberChoices]
  );

  const propertiesMode: 'columns' | 'chips' = tab === 'table' ? 'columns' : 'chips';
  const propertyChoices: ReadonlyArray<PropertyChoice> = useMemo(() => {
    if (propertiesMode === 'columns') {
      return WORK_ITEM_COLUMNS.map((c) => ({ key: c.key, label: c.label }));
    }
    return BOARD_CHIP_KEYS.map((k) => ({
      key: k,
      label: BOARD_CHIP_LABELS[k] ?? k
    }));
  }, [propertiesMode]);
  const propertyLockedKeys =
    propertiesMode === 'columns' ? LOCKED_COLS : ([] as ReadonlyArray<string>);

  return (
    <>
      <ViewTabs
        options={TAB_ORDER.map((t) => ({ key: t, label: TAB_LABELS[t] }))}
        current={tab}
        onChange={(next) => setTab(next as WorkItemsTab)}
        ariaLabel="Work items views"
        testIdPrefix={testIdPrefix}
      />
      <ListControlBar
        testIdPrefix={controlBarTestIdPrefix}
        activeTab={tab}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search work items…"
        searchAriaLabel="Search work items"
        filters={filters}
        onFiltersChange={(next) => setFilters(next as typeof filters)}
        facets={facets}
        groupByOptions={GROUP_BY_OPTIONS as ReadonlyArray<GroupByOption>}
        groupBy={groupBy}
        onGroupByChange={(next) => setGroupBy(next as typeof groupBy)}
        groupByDisabledKeys={tab === 'board' ? ['status'] : undefined}
        propertiesMode={propertiesMode}
        propertyChoices={propertyChoices}
        propertyLockedKeys={propertyLockedKeys}
        visibleProperties={visibleCols}
        onVisiblePropertiesChange={setVisibleCols}
      />
      <section
        className="work-items-route__panel"
        role="tabpanel"
        aria-label={`${TAB_LABELS[tab]} view`}
        data-testid={`${testIdPrefix}-panel`}
        data-tab={tab}
      >
        {tab === 'table' ? (
          <WorkItemsTableTab
            filters={filters}
            groupBy={groupBy}
            urlAdapter={tableUrlAdapter}
            visibleCols={visibleCols}
            activeOrgId={activeOrgId}
            projectId={projectId}
            epicChoices={epicChoices}
            onToast={onToast}
            onOpenItem={onOpenItem}
            reloadSignal={reloadSignal}
            testIdPrefix={`${testIdPrefix}-table`}
          />
        ) : tab === 'timeline' ? (
          <div data-testid={`${testIdPrefix}-timeline-placeholder`}>
            Coming soon
          </div>
        ) : (
          <WorkItemsBoardTab
            filters={filters}
            groupBy={groupBy}
            visibleCols={visibleCols}
            activeOrgId={activeOrgId}
            projectId={projectId}
            onOpenItem={onOpenItem}
            reloadSignal={reloadSignal}
          />
        )}
      </section>
    </>
  );
}
